/**
 * Achievement Service
 * Centralizes all achievement-related API operations
 */

import apiService from './api';

/**
 * Get all user achievements with unlock status
 * @returns {Promise<Array>} User's achievements with unlock status and notifications
 */
const getUserAchievements = async () => {
  const response = await apiService.get('/api/achievements');
  return response.json();
};

/**
 * Get public achievements for a specific user by username
 * @param {string} username - Username to get achievements for
 * @returns {Promise<Array>} Public achievements for the user
 */
const getPublicUserAchievements = async (username) => {
  const response = await apiService.get(`/api/achievements/public/user/${username}`);
  return response.json();
};

/**
 * Get all public achievements for a specific user by user ID
 * @param {string} userId - User ID to get achievements for  
 * @returns {Promise<Object>} All public achievements for the user
 */
const getPublicUserAchievementsById = async (userId) => {
  const response = await apiService.get(`/api/achievements/public/user/${userId}/all`);
  return response.json();
};

/**
 * Get all available achievements (system-wide)
 * @returns {Promise<Array>} All achievements in the system
 */
const getAllAchievements = async () => {
  const response = await apiService.get('/api/achievements/all');
  return response.json();
};

/**
 * Mark achievements as notified (remove notification badges)
 * @param {Array} achievementIds - Array of achievement IDs to mark as notified
 * @returns {Promise<Object>} Operation result
 */
const markAchievementsAsNotified = async (achievementIds) => {
  const response = await apiService.post('/api/achievements/mark-notified', {
    achievementIds
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to mark achievements as notified');
  }
  
  // Check Content-Type before parsing to ensure robustness
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return await response.json();
  }
  
  // If not JSON, return success indicator (fallback for future compatibility)
  return { success: true };
};

/**
 * Get achievement details by ID
 * @param {string} achievementId - Achievement ID
 * @returns {Promise<Object>} Achievement details
 */
const getAchievementDetails = async (achievementId) => {
  const response = await apiService.get(`/api/achievements/${achievementId}`);
  return response.json();
};

/**
 * Check for new achievements (typically called after game completion)
 * @returns {Promise<Array>} Newly unlocked achievements
 */
const checkNewAchievements = async () => {
  const response = await apiService.get('/api/achievements/check-new');
  return response.json();
};

/**
 * Get user's selected badge achievement
 * @returns {Promise<Object|null>} Selected badge achievement or null
 */
const getSelectedBadge = async () => {
  const response = await apiService.get('/api/achievements/selected-badge');
  return response.json();
};

/**
 * Update user's selected badge
 * @param {string} achievementId - Achievement ID to set as badge, or null to clear
 * @returns {Promise<Object>} Operation result
 */
const updateSelectedBadge = async (achievementId) => {
  const response = await apiService.put('/api/achievements/selected-badge', {
    achievementId
  });
  return response.json();
};

/**
 * Get achievement progress for user
 * @returns {Promise<Object>} Achievement progress data
 */
const getAchievementProgress = async () => {
  const response = await apiService.get('/api/achievements/progress');
  return response.json();
};

/**
 * Get achievements by category
 * @param {string} category - Achievement category (e.g., 'daily-streak', 'total-wins', 'user-groups', 'other')
 * @returns {Promise<Array>} Achievements in the specified category
 */
const getAchievementsByCategory = async (category) => {
  const response = await apiService.get(`/api/achievements/category/${category}`);
  return response.json();
};

/**
 * Get leaderboard of users with most achievements
 * @param {number} [limit=10] - Number of top users to return
 * @returns {Promise<Array>} Achievement leaderboard
 */
const getAchievementLeaderboard = async (limit = 10) => {
  const response = await apiService.get(`/api/achievements/leaderboard?limit=${limit}`);
  return response.json();
};

export default {
  getUserAchievements,
  getPublicUserAchievements,
  getPublicUserAchievementsById,
  getAllAchievements,
  markAchievementsAsNotified,
  getAchievementDetails,
  checkNewAchievements,
  getSelectedBadge,
  updateSelectedBadge,
  getAchievementProgress,
  getAchievementsByCategory,
  getAchievementLeaderboard
};